import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import './MockExamRegistration.css';

import { Button, LoadingScroll, PopUp, Snackbar } from 'geniebook-ui/dist';
import { ErrorObject } from '@models/interfaces/global.interface';
import ArrowLeft from '@assets/icons/arrow-left';

import AuthLayout from '@layouts/AuthLayout';
import Preloading from '@components/Preloading';
import {
  PAGE_WEBINAR_SELECT,
  PAGE_WEBINAR_PAYMENT,
  PAGE_WEBINAR_WELCOME,
  PAGE_WEBINAR_THANK_YOU,
  TRANSACTION_STATUS_PENDING,
  TRANSACTION_STATUS_SUCCESS,
  TRANSACTION_STATUS_UNPAID,
  CONTACT_SUPPORT_URL,
  PAGE_WEBINAR_CLOSED,
} from '@utils/constants/mock-exam.constants';

import {
  ILastTransaction,
  IWebinarParentData,
  WebinarSchedule,
} from '@models/interfaces/mock_exam.interface';
import MockExamService from '@services/MockExamServices.service';
import PaymentForm, {
  IPaymentFormData,
} from '@components/mock_exam/PaymentForm';
import WebinarItems, {
  IWebinarItems,
} from '@components/mock_exam/WebinarItems';
import WebinarGetStarted from '@components/mock_exam/WebinarGetStarted';

import BookWebinar from '@components/mock_exam/BookWebinar';
import WebinarThankYouPage from '@components/mock_exam/WebinarThankYouPage';
import WebinarClosed from '@components/mock_exam/WebinarClosed';
import moment from 'moment';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

const printLeadingZero = (number: number, maxDigit: number) => {
  // Convert the number to a string
  let numberStr = number.toString();

  // Calculate the number of leading zeros required
  const leadingZeros = maxDigit - numberStr.length;

  // Add leading zeros if necessary
  if (leadingZeros > 0) {
    numberStr = '0'.repeat(leadingZeros) + numberStr;
  }

  // Return the 7-digit number as a string
  return numberStr;
};

const WebinarRegistration = () => {
  const webinarLastDay = new Date(2023, 8, 6, 21, 0, 0); // 06 September 9PM GMT+8
  const nowMoment = moment(new Date()); // Now Time in local Timezone

  // for detecting timezone offset https://stackoverflow.com/a/8090836
  const timezone = parseInt(
    (-(new Date().getTimezoneOffset() / 60)).toString(),
  );
  nowMoment.hour(nowMoment.hour() - timezone + 8); // Now Time in GMT+8
  const isWebinarClosed: boolean =
    nowMoment.toDate().getTime() > webinarLastDay.getTime();

  const [step, setStep] = useState<string>(
    isWebinarClosed ? PAGE_WEBINAR_CLOSED : PAGE_WEBINAR_WELCOME,
  );
  const [viewWidth, setViewWidth] = useState<number>(0);
  const [isLoadingOnLoad, setIsLoadingOnLoad] = useState<boolean>(true);
  const [loading] = useState<boolean>(false);
  const [showPaymentFailed, setShowPaymentFailed] = useState<boolean>(false);
  const [error, setError] = useState<ErrorObject>({
    error: false,
    error_msg: '',
    error_code: '',
    error_state: 'error',
  });
  const [transactionData, setTransactionData] =
    useState<ILastTransaction | null>();
  const navigate = useNavigate();

  // too make sure snack bar placed correctly
  useEffect(() => {
    window.addEventListener('resize', () => setViewWidth(window.innerWidth));
    setViewWidth(window.innerWidth);
    return () => {
      window.removeEventListener('resize', () =>
        setViewWidth(window.innerWidth),
      );
    };
  }, []);

  useEffect(() => {
    setError({
      error: false,
      error_msg: '',
      error_code: '',
    });
    MockExamService.visitTracker(false, step);
  }, [step]);

  // Parent data
  const [parentData, setParentData] = useState<IWebinarParentData>({
    parent_info_id: '0',
    name: '',
    email: '',
    address: '',
    postal_code: '',
  });

  const [groupedWebinarList, setGroupedWebinarList] = useState<{
    [key: number]: WebinarSchedule[];
  }>([]);
  const [sortedKey, setSortedKey] = useState<string[]>([]);

  // fetch parent data
  const { id } = useParams();
  useQuery(['data', id], () => MockExamService.getWebinarParentData(id), {
    onSuccess(data) {
      if (data.data.error == true) {
        navigate('/404');
      } else {
        setParentData(data.data.data.parent_detail);

        // group obtained schedule list by level id
        const tempWebinarList = data.data.data.schedules.reduce(
          (groups: { [key: number]: WebinarSchedule[] }, item) => {
            if (!groups[item.level_id]) {
              groups[item.level_id] = [];
            }
            groups[item.level_id].push(item);
            return groups;
          },
          {},
        );
        setGroupedWebinarList(tempWebinarList);

        // sort the keys, used for guidance when printing the schedule in the UI
        setSortedKey(
          Object.keys(tempWebinarList).sort((a, b) => a.localeCompare(b)),
        );

        // check transction status
        const lastTransaction = data.data.data.last_transaction;
        if (lastTransaction != null) {
          if (
            lastTransaction.transaction_status == TRANSACTION_STATUS_PENDING ||
            lastTransaction.transaction_status == TRANSACTION_STATUS_SUCCESS
          )
            setStep(PAGE_WEBINAR_THANK_YOU);
          else {
            if (lastTransaction.transaction_status != TRANSACTION_STATUS_UNPAID)
              setShowPaymentFailed(true);
            setStep(PAGE_WEBINAR_PAYMENT);
          }
        }

        setTransactionData(lastTransaction);
        setIsLoadingOnLoad(false);
      }
    },
    onError() {
      navigate('/404');
    },
  });

  const createPaymentData = () => {
    const paymentData: IPaymentFormData = {
      parentInfoId: parentData.parent_info_id,
      currencyCode: groupedWebinarList[parseInt(sortedKey[0])][0].currency_code,
      amountPaid: 0,
      classIds: [],
      address: parentData.address,
      postalCode: parentData.postal_code,
    };

    for (const level_id in groupedWebinarList) {
      if (!Object.prototype.hasOwnProperty.call(groupedWebinarList, 'level_id'))
        continue;
      for (const schedule of groupedWebinarList[level_id]) {
        if (schedule.will_register === true) {
          paymentData.amountPaid += schedule.price;
          paymentData.classIds.push(schedule.class_id);
        }
      }
    }

    return paymentData;
  };

  const createWebinarItems = () => {
    // only take selected webinar items
    const items: IWebinarItems = {
      groupedItems: [],
      sortedKey: [],
    };

    for (const level_id in groupedWebinarList) {
      if (!Object.prototype.hasOwnProperty.call(groupedWebinarList, 'level_id'))
        continue;
      const tempArray = [];
      for (const schedule of groupedWebinarList[level_id]) {
        if (schedule.will_register === true) {
          tempArray.push(schedule);
        }
      }
      if (tempArray.length > 0) items.groupedItems[level_id] = tempArray;
    }

    items.sortedKey = Object.keys(items.groupedItems).sort((a, b) =>
      a.localeCompare(b),
    );
    return items;
  };

  return (
    <>
      <Helmet>
        <title>Geniebook</title>
        <meta name="description" content="Geniebook" />
      </Helmet>
      <AuthLayout
        illustration=""
        className={`mock-exam-layout ${
          step == PAGE_WEBINAR_PAYMENT &&
          'bg-neutral-sketchbook !flex-col webinar-payment'
        } ${step == PAGE_WEBINAR_WELCOME && '!flex-col webinar-welcome'}`}
        animationContainerClassName={`${
          step == PAGE_WEBINAR_PAYMENT && '!flex-col'
        }`}
        animationContent={
          step == PAGE_WEBINAR_PAYMENT ? (
            <div className="webinar-payment-separator max-lg:pb-6 max-lg:px-16 max-sm:px-4">
              <div
                onClick={() => {
                  setStep(PAGE_WEBINAR_SELECT);
                }}
                className="text-primary-apricot mb-4 underline flex flex-row item-center gap-1 hover:text-primary-skyblue hover:cursor-pointer mt-10 max-lg:mt-6"
              >
                <div className="pt-[6px]">
                  <ArrowLeft className="w-[14px] h-4 inline-block fill-current" />
                </div>
                <div>Back to select sessions</div>
              </div>
              <WebinarItems items={createWebinarItems()} />
            </div>
          ) : (
            <div className="webinar-left-container">
              <div className="webinar-left-content"></div>
            </div>
          )
        }
      >
        <>
          <div className="w-full">
            <div className="container-form">
              {step == PAGE_WEBINAR_WELCOME && (
                <WebinarGetStarted setStep={setStep} parentData={parentData} />
              )}
              {step == PAGE_WEBINAR_SELECT && (
                <BookWebinar
                  scheduleList={groupedWebinarList}
                  setScheduleList={setGroupedWebinarList}
                  sortedKey={sortedKey}
                  setError={setError}
                  setStep={setStep}
                />
              )}
              {step == PAGE_WEBINAR_PAYMENT && (
                <PaymentForm
                  initialPaymentData={createPaymentData()}
                  setError={setError}
                />
              )}
              {step == PAGE_WEBINAR_THANK_YOU &&
              transactionData?.subscription_transaction_id ? (
                <WebinarThankYouPage
                  parentName={parentData.name}
                  parentEmail={parentData.email}
                  orderNumber={
                    '2023-WEB-' +
                    printLeadingZero(
                      transactionData?.subscription_transaction_id,
                      6,
                    )
                  }
                  setStep={setStep}
                />
              ) : null}
              {step == PAGE_WEBINAR_CLOSED && <WebinarClosed />}
            </div>
          </div>
        </>
      </AuthLayout>
      {loading && <LoadingScroll isBlockOthers={true} />}
      {error.error_msg !== '' && (
        <Snackbar
          message={error.error_msg}
          views={viewWidth < breakpoint.md ? 'mobile' : 'desktop'}
          buttonText={'Dismiss'}
          onTextClick={() =>
            setError({
              error: false,
              error_msg: '',
              error_code: '',
            })
          }
          states={error.error_state == undefined ? 'error' : error.error_state}
          className={'fixed center-horizontal top-[60px] webinar-snackbar'}
        />
      )}
      {isLoadingOnLoad && <Preloading className="z-10" />}
      {showPaymentFailed && (
        <div className="payment-failed-popup">
          <PopUp
            title={'Payment Failed'}
            description={
              "Your payment couldn't be processed. Please try again or contact our support team for assistance."
            }
            buttonGroup={
              <>
                <Button
                  variant="primary"
                  onClick={() => setShowPaymentFailed(false)}
                  className="max-w-full"
                >
                  Got it
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.open(CONTACT_SUPPORT_URL, '_blank')}
                  className="max-w-full"
                >
                  Contact support team
                </Button>
              </>
            }
            onClose={() => {}}
            disableBodyScroll={true}
            width="368px"
          />
        </div>
      )}
    </>
  );
};

export default WebinarRegistration;
